import Drawer from '@/components/Drawer';
import { useAuthCheck } from '@/hooks/useAuthCheck';
import { useDimensions } from '@/hooks/useDimensions';
import { useAudioListStore, useIsPlayAudioInview } from '@/state/audio-list-store';
import { useLeftPanelDrawer, useProfileDrawer, useSongDetailDrawer } from '@/state/drawer-store';
import { cn } from '@/utils';
import AudioList from './components/AudioList';
import Catalog from './components/Catalog';
import Folders from './components/Folders';
import MiniPlayer from './components/MiniPlayer';
import { Profile } from './components/Profile';
import { SongDetail } from './components/SongDetail';
import { GenerateWithQuery } from './components/GenerateWithQuery';
import Generator from './Generator';
import { useGetBreadName } from '@/hooks/useGetBreadName';
import { ChevLeftFilledIcon, CloseFilledIcon, JenLogoIcon, MenuIcon } from '@/components/Icons';
import useGeneratorStore from '@/state/generator-store';
import LinksDetail from './components/LinksDetail';
import { useLinksDetailShow, useNewUserFlowStartShow } from '@/state/show-store';
import { useUserNameDialog } from '@/hooks/useUsernameDialog';
import { useEffect, useRef } from 'react';
import { useSessionStore } from '@/hooks/useSessionStore';
import { NewUserInstruction, GenerateInstruction } from './components/Instruction';
import { useCatalogOverviewStore } from '@/state/catalog-store';
import { useRefreshData } from '@/hooks/useRefreshData';
import { useSearchParams } from 'react-router-dom';
import SideBar from '@/components/SideBar';
import useFlags from '@jenmusic/feature-flag';
import { useAudioPlayerStore } from '@/state/audio-player-store';

const GENERATED_TRACK_ID_LEN = 36;

const Home = () => {
  const { setIsDrawerShow } = useLeftPanelDrawer();
  const { selectedInfo } = useAudioListStore();
  const { isPlayAudioInview } = useIsPlayAudioInview();
  const { playAudioInfo } = useAudioPlayerStore();
  const isPlayGeneratedTrack = playAudioInfo?.id?.length === GENERATED_TRACK_ID_LEN;
  const { isAuth } = useAuthCheck();
  const { isMobileOrPad } = useDimensions();
  const { firstPathName } = useGetBreadName();
  const { catalogOverviewStore } = useCatalogOverviewStore();
  const { refreshCatalogOverview } = useRefreshData();
  useEffect(() => {
    refreshCatalogOverview();
  }, []);

  const { setIsShow: setLinkDetailShow } = useLinksDetailShow();

  const { showNewNavigationBar } = useFlags();

  const { isGeneratorDrawerShow } = useGeneratorStore((state) => ({
    isGeneratorDrawerShow: state.isGeneratorDrawerShow,
  }));

  const { showUsernameDialog } = useUserNameDialog();
  const { session } = useSessionStore();

  const { setIsShow: setNewUserFlowStart } = useNewUserFlowStartShow();
  const isNewUser =
    !!Object.values(catalogOverviewStore).length && Object.values(catalogOverviewStore).every((catalogNum) => catalogNum === 0);

  const [searchParams] = useSearchParams();
  const prompt = searchParams.get('prompt') || '';

  useEffect(() => {
    if (session.user?.id) {
      if (!prompt) {
        if (!session.user?.name) {
          showUsernameDialog(() => {
            setNewUserFlowStart(true);
          });
        } else {
          if (isNewUser) {
            setNewUserFlowStart(true);
          }
        }
      }
    }
  }, [session.user?.id, session.user?.name, isNewUser]);

  useEffect(() => {
    (async () => {
      if (!isAuth) return;
      const discordToken = localStorage.getItem('discordToken');
      if (discordToken) {
        localStorage.removeItem('discordToken');
        window.location.replace(`/discord?token=${discordToken}`);
      }
    })();
  }, [isAuth]);

  return (
    <>
      <GenerateWithQuery />
      {isMobileOrPad && (
        <>
          <div className="w-full h-full">
            <div className={`grid w-full h-full gap-[1px] grid-cols-1 bg-jen-background-secondary grid-rows-[52px_minmax(0,1fr)_96px]`}>
              <div className="flex border-b border-jen-border">
                <div
                  onClick={() => setIsDrawerShow(true)}
                  className="border-r border-jen-border flex-1 flex items-center text-jen-content-secondary text-[14px]"
                >
                  <ChevLeftFilledIcon fillColor="currentColor" className="relative top-[-1.5px]" />
                  {firstPathName}
                </div>
                <div className="w-[52px] h-[52px] flex justify-center items-center">
                  <div
                    className="bg-jen-background-secondary flex justify-center items-center"
                    onClick={() => {
                      setLinkDetailShow(true);
                    }}
                  >
                    <MenuIcon fillColor="#67666d" className="text-jen-grey-3 cursor-pointer" />
                    {/* <ChevronRight className="text-jen-grey-3 cursor-pointer" /> */}
                  </div>
                </div>
              </div>
              <div className=" bg-white lg:row-span-1 relative z-[1]">
                <AudioList />
                {/* { only show not in view & generated track} */}
                {!isPlayAudioInview && isPlayGeneratedTrack && (
                  <MiniPlayer className="lg:hidden h-[42px] w-[calc(100vw-16px)] bg-jen-background-primary absolute left-[8px] bottom-[8px] z-[1]" />
                )}
              </div>
              <Generator />
            </div>
          </div>
          <LeftPanelDrawer />
          <ProfileDrawer />
          <SongDetailDrawer />
          <LinkDetailDrawer />
        </>
      )}
      {!isMobileOrPad && (
        <>
          <div className="w-full h-full">
            <div
              className={`grid w-full h-full grid-cols-2 lg:grid-cols-[352px_1fr_352px] gap-0 lg:gap-[1px]
            ${isGeneratorDrawerShow ? 'grid-rows-[minmax(0,1fr)_400px] 2xl:grid-rows-[minmax(0,1fr)_400px]' : 'grid-rows-[minmax(0,1fr)_110px]'}
            relative
          `}
            >
              <>
                <div className="hidden lg:block col-span-1  row-span-1 bg-white ">
                  <LeftPanel />
                </div>
                <div className="col-span-2 lg:col-span-1 bg-white lg:row-span-1">
                  <AudioList />
                </div>
                <div
                  className={cn('hidden lg:grid bg-jen-border  lg:row-span-1 lg:grid-rows-[72px_1fr] gap-[1px]', {
                    'lg:grid-rows-[1fr]': showNewNavigationBar,
                  })}
                >
                  {!showNewNavigationBar && <Profile />}
                  <div className="w-full h-full overflow-hidden bg-jen-background-primary relative">
                    <div
                      className={cn('w-full h-full  bg-jen-background-primary overflow-hidden', {
                        ' ml-[-1px]': Boolean(selectedInfo),
                      })}
                    >
                      {selectedInfo && <SongDetail />}
                    </div>
                  </div>
                </div>
              </>
              {isNewUser ? (
                <div className="fixed left-0 top-0 w-[100vw] h-[calc(100vh-110px)] bg-white z-10 opacity-80">{/* <NewUserPage /> */}</div>
              ) : null}
              <Generator />
            </div>
          </div>
          {isNewUser && <NewUserInstruction />}

          <GenerateInstruction />
        </>
      )}
    </>
  );
};

const LeftPanelDrawer = () => {
  const { isDrawerShow, setIsDrawerShow } = useLeftPanelDrawer();
  const { isMobileOrPad } = useDimensions();
  if (!isMobileOrPad) {
    return null;
  }
  return (
    <Drawer className="bg-white" side="left" width="90vw" isOpen={isDrawerShow} onClose={() => setIsDrawerShow(false)}>
      <LeftPanel />
    </Drawer>
  );
};

const ProfileDrawer = () => {
  const { isDrawerShow, setIsDrawerShow } = useProfileDrawer();
  const { isMobileOrPad } = useDimensions();
  if (!isMobileOrPad) {
    return null;
  }
  return (
    <Drawer className="bg-white" side="right" width="90vw" isOpen={isDrawerShow} onClose={() => setIsDrawerShow(false)}>
      <Profile />
    </Drawer>
  );
};

const LinkDetailDrawer = () => {
  const { isShow: isLinksDetailShow, setIsShow: setIsDrawerShow } = useLinksDetailShow();
  const { isMobileOrPad } = useDimensions();
  const { showNewNavigationBar } = useFlags();
  if (!isMobileOrPad) {
    return null;
  }
  return (
    <Drawer className="bg-white" side="right" width="100vw" isOpen={isLinksDetailShow} onClose={() => setIsDrawerShow(false)}>
      {showNewNavigationBar ? (
        <div className="w-full h-full flex flex-col relative">
          <CloseFilledIcon onClick={() => setIsDrawerShow(false)} className=" absolute right-2 top-2 box-content p-[14px]" />

          <div className="w-full h-full  bg-jen-background-primary">
            <SideBar clickNavigationCallback={() => setIsDrawerShow(false)} />
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col">
          <div className="flex justify-between pl-[12px] h-[52px] items-center border-b border-jen-border">
            <JenLogoIcon />
            <CloseFilledIcon onClick={() => setIsDrawerShow(false)} className=" box-content p-[14px] border-l border-jen-border" />
          </div>
          <div className="w-full h-full  bg-jen-background-primary">
            <LinksDetail />
          </div>
        </div>
      )}
    </Drawer>
  );
};
const SongDetailDrawer = () => {
  const { isDrawerShow, setIsDrawerShow } = useSongDetailDrawer();
  const { isMobileOrPad } = useDimensions();
  if (!isMobileOrPad) {
    return null;
  }
  return (
    <Drawer
      className={'bg-jen-background-primary'}
      side="right"
      width="90vw"
      isOpen={isDrawerShow}
      onClose={() => {
        setIsDrawerShow(false);
      }}
    >
      <SongDetail
        onClose={() => {
          setIsDrawerShow(false);
        }}
      />
    </Drawer>
  );
};

export const LeftPanel = () => {
  const { isMobileOrPad } = useDimensions();
  const { isPlayAudioInview } = useIsPlayAudioInview();
  const { playAudioInfo } = useAudioPlayerStore();
  const isPlayGeneratedTrack = playAudioInfo?.id?.length === GENERATED_TRACK_ID_LEN;

  return (
    <div className="w-full h-full p-jen-spacing-16 flex flex-col justify-between">
      <div className="flex flex-col h-full">
        <div>
          <Catalog />
        </div>
        <div className="flex-1 h-full min-h-0">
          <Folders />
        </div>
        {!isMobileOrPad && !isPlayAudioInview && isPlayGeneratedTrack && <MiniPlayer />}
      </div>
    </div>
  );
};

export default Home;
